import React, { createContext, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { enterDealerScope, getCurrentTenant } from '../../services/tenantScope';
import { SettingsProvider } from '../../hooks/useSettings';
import { invalidate as invalidateSettingsLists } from '../../services/settingsListCache';
import { useOptionalAuth } from '../../contexts/AuthContext';

/**
 * IDI-63: a super-admin's workspace for ONE dealer, at
 * /super-admin/dealers/<id>/…. The api.js interceptor sends <id> as
 * X-Tenant-Id from the URL (D1); this context scopes the tenant caches to the
 * dealer (F3), gives Settings its base path, and lets pages hide the
 * dealer-only actions (Add User, Invite, Resend invite, Edit in Quote Builder).
 */
const DealerWorkspaceContext = createContext(null);

export function DealerWorkspaceProvider({ dealerId, children }) {
  // Children mount only once the caches are scoped to this dealer, so no
  // page fills a cache under the signed-in key with the dealer's data.
  const [scoped, setScoped] = useState(null);
  // IDI-622 (D5): leaving the dealer's page reloads IDI's own settings lists in
  // the app-level SettingsProvider (it is keyed on the signed-in identity only,
  // so it would keep the dealer's lists). Read through a ref so the effect
  // doesn't re-run on auth changes; skipped when signed out (the silent tenant
  // reset exists to avoid a request storm on sign-out).
  const auth = useOptionalAuth();
  const authRef = useRef(auth);
  authRef.current = auth;
  useLayoutEffect(() => {
    const restore = enterDealerScope(dealerId);
    const scopeKey = getCurrentTenant();
    setScoped(dealerId);
    return () => {
      // Sign-out nulls the tenant before this page unmounts: no reload then.
      const signedOut = getCurrentTenant() !== scopeKey
        || (authRef.current && !authRef.current.isAuthenticated);
      restore();
      if (!signedOut) invalidateSettingsLists();
    };
  }, [dealerId]);
  const value = useMemo(
    () => ({ dealerId, basePath: `/super-admin/dealers/${encodeURIComponent(dealerId)}/settings` }),
    [dealerId]
  );
  return (
    <DealerWorkspaceContext.Provider value={value}>
      {scoped === dealerId && <SettingsProvider key={dealerId}>{children}</SettingsProvider>}
    </DealerWorkspaceContext.Provider>
  );
}

/** { dealerId, basePath } inside a dealer workspace; null everywhere else. */
export function useDealerWorkspace() {
  return useContext(DealerWorkspaceContext);
}

/** Where Settings lives: '/settings', or the dealer workspace's settings. */
function useSettingsBasePath() {
  const ws = useContext(DealerWorkspaceContext);
  return ws ? ws.basePath : '/settings';
}

/** Rewrites a '/settings/…' path to the current Settings base. */
export function useSettingsHref() {
  const base = useSettingsBasePath();
  return (path) => (path.startsWith('/settings') ? base + path.slice('/settings'.length) : path);
}
